import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "../api";
import "./Home.css";

export default function Home() {
  const [images, setImages] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [stats] = useState({
    members: 100,
    events: 5,
    years: 2
  });
  const navigate = useNavigate();

  // Fetch images from backend
  useEffect(() => {
    const fetchImages = async () => {
      try {
        const response = await api.get("members/home-images/");
        const imageUrls = response.data.map((img) => img.image);
        setImages(imageUrls);
      } catch (error) {
        console.error("Error fetching home images:", error);
      }
    };
    fetchImages();
  }, []);

  // Auto-slide every 5 seconds
  useEffect(() => {
    if (images.length === 0) return;
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % images.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [images]);

  const goToSlide = (index) => {
    setCurrentIndex(index);
  };

  const nextSlide = () => {
    setCurrentIndex((prev) => (prev + 1) % images.length);
  };

  const prevSlide = () => {
    setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  if (images.length === 0) {
    return (
      <div className="home-container">
        <div className="loading-home">
          <div className="spinner"></div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="home-container">
      {/* ===== Hero Image Slider ===== */}
      <div className="hero-section">
        <div className="hero-slider">
          {images.map((img, index) => (
            <img
              key={index}
              src={img}
              alt={`Community ${index + 1}`}
              className={`hero-image ${index === currentIndex ? 'active' : ''}`}
            />
          ))}
        </div>

        <div className="hero-overlay">
          <div className="hero-content">
            <h1 className="hero-title">Welcome to the Marakwet Community in Australia</h1>
            <p className="hero-subtitle">Together in Unity, Welfare, and Development</p>
            <div className="hero-buttons">
              <button onClick={() => navigate('/membership')} className="btn-primary">
                Join Us Today
              </button>
              <button onClick={() => navigate('/about')} className="btn-secondary">
                Learn More
              </button>
            </div>
          </div>
        </div>

        {/* Slider Controls */}
        {images.length > 1 && (
          <>
            <button className="slider-btn prev" onClick={prevSlide}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <polyline points="15 18 9 12 15 6" strokeWidth="2"/>
              </svg>
            </button>
            <button className="slider-btn next" onClick={nextSlide}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <polyline points="9 18 15 12 9 6" strokeWidth="2"/>
              </svg>
            </button>

            <div className="slider-dots">
              {images.map((_, index) => (
                <button
                  key={index}
                  className={`dot ${index === currentIndex ? 'active' : ''}`}
                  onClick={() => goToSlide(index)}
                />
              ))}
            </div>
          </>
        )}
      </div>

      {/* ===== Stats Section ===== */}
      <section className="stats-section">
        <div className="stats-container">
          <div className="stat-card">
            <div className="stat-number">{stats.members}+</div>
            <div className="stat-label">Active Members</div>
          </div>
          <div className="stat-card">
            <div className="stat-number">{stats.events}+</div>
            <div className="stat-label">Annual Events</div>
          </div>
          <div className="stat-card">
            <div className="stat-number">{stats.years}+</div>
            <div className="stat-label">Years of Service</div>
          </div>
        </div>
      </section>

      {/* ===== Introduction Section ===== */}
      <section className="intro-section">
        <div className="section-header">
          <h2>Who We Are</h2>
          <div className="section-underline"></div>
        </div>
        <p className="intro-text">
          The <strong>Marakwet Community in Australia (MCIA)</strong> unites
          Marakwet members across Australia, building strong social ties and
          mutual support. We are driven by togetherness, compassion, and
          progress — preserving our heritage while shaping a better future.
        </p>
      </section>

      {/* ===== Features Section ===== */}
      <section className="features-section">
        <div className="section-header">
          <h2>What We Offer</h2>
          <div className="section-underline"></div>
        </div>
        <div className="features-grid">
          <div className="feature-card">
            <div className="feature-icon">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" strokeWidth="2"/>
                <circle cx="9" cy="7" r="4" strokeWidth="2"/>
                <path d="M23 21v-2a4 4 0 0 0-3-3.87" strokeWidth="2"/>
                <path d="M16 3.13a4 4 0 0 1 0 7.75" strokeWidth="2"/>
              </svg>
            </div>
            <h3>Connect</h3>
            <p>
              Build lasting friendships and professional connections through
              social gatherings and networking opportunities.
            </p>
          </div>

          <div className="feature-card">
            <div className="feature-icon">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2" strokeWidth="2"/>
                <line x1="16" y1="2" x2="16" y2="6" strokeWidth="2"/>
                <line x1="8" y1="2" x2="8" y2="6" strokeWidth="2"/>
                <line x1="3" y1="10" x2="21" y2="10" strokeWidth="2"/>
              </svg>
            </div>
            <h3>Events</h3>
            <p>
              Attend cultural celebrations, welfare drives, and exciting
              community events all year round.
            </p>
          </div>

          <div className="feature-card">
            <div className="feature-icon">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <circle cx="12" cy="12" r="10" strokeWidth="2"/>
                <line x1="2" y1="12" x2="22" y2="12" strokeWidth="2"/>
                <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" strokeWidth="2"/>
              </svg>
            </div>
            <h3>Development</h3>
            <p>
              Participate in educational and empowerment initiatives that make
              a positive impact both in Australia and back home.
            </p>
          </div>
        </div>
      </section>

      {/* ===== Call to Action Section ===== */}
      <section className="cta-section">
        <div className="cta-content">
          <h2>Ready to Join Our Community?</h2>
          <p>Become part of a vibrant community dedicated to unity, welfare, and development.</p>
          <button onClick={() => navigate('/membership')} className="cta-button">
            Become a Member
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <line x1="5" y1="12" x2="19" y2="12" strokeWidth="2"/>
              <polyline points="12 5 19 12 12 19" strokeWidth="2"/>
            </svg>
          </button>
        </div>
      </section>

      {/* ===== Mission & Vision ===== */}
      <section className="mission-section">
        <div className="mission-grid">
          <div className="mission-card">
            <h3>Our Mission</h3>
            <p>
              To foster a strong, supportive community that preserves Marakwet culture
              while empowering members to thrive in Australia through unity, welfare,
              and collaborative development initiatives.
            </p>
          </div>
          <div className="mission-card">
            <h3>Our Vision</h3>
            <p>
              A united and prosperous Marakwet community in Australia that serves as
              a model of cultural preservation, mutual support, and positive impact
              both locally and in our homeland.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}